<template>
  <div class="data-table">
    <!-- SPINNER -->
    <div
      class="d-flex justify-content-center align-items-center spinner-container"
      v-if="isLoading"
    >
      <img src="@/assets/gif/spinner.gif" width="200" height="200" />
    </div>

    <template v-else>
      <DataTable
        v-if="!showSlot"
        :value="tableData"
        :paginator="false"
        class="p-datatable-customers"
        :rows="10"
        dataKey="id"
        :rowHover="true"
        responsiveLayout="scroll"
      >
        <!-- Header toolbar -->
        <template #header>
          <div class="data-table__header">
            <!-- Slot para el botón de creación -->
            <slot name="create-button">
              <b-button
                v-if="showCreateButton"
                class="data-table__header--button"
                @click="$emit('create')"
              >{{ createLabel }}</b-button>
            </slot>

            <!-- Slot para los filtros -->
            <div class="data-table__container-filter">
              <div
                v-if="showSearch"
                class="p-inputgroup data-table__container-filter-search"
              >
                <InputText
                  :placeholder="searchPlaceholder"
                  v-model="internalSearch"
                  class="data-table__container-filter-search--input-text"
                  v-on:keyup.enter="onSearch"
                />
                <Button
                  icon="pi pi-times"
                  class="p-button data-table__container-filter-search--button-clear"
                  @click="onClear"
                />
                <Button
                  icon="pi pi-search"
                  class="data-table__container-filter-search--button"
                  @click="onSearch"
                />
              </div>

              <slot name="filters" />
            </div>
          </div>
        </template>

        <template #empty> No files found. </template>

        <!-- Columnas inyectadas por el componente hijo -->
        <slot name="columns" />

        <!-- Columna de acciones genérica, personalizable -->
        <slot name="actions-column" />
      </DataTable>

      <!-- Slot para vistas alternativas (ej. editor de CannedResponses) -->
      <slot name="alt-view" />
    </template>

    <b-pagination
      v-if="!isLoading && !showSlot"
      v-model="internalPage"
      pills
      :total-rows="totalItemsCount"
      :per-page="perPage"
      class="my-0"
      @change="$emit('paginate', $event)"
      aria-controls="data"
    />
  </div>
</template>

<script>
export default {
  name: "BaseTable",
  props: {
    isLoading: { type: Boolean, default: false },
    tableData: { type: Array, default: () => [] },
    totalItemsCount: { type: Number, default: 0 },
    perPage: { type: Number, default: 15 },
    currentPage: { type: Number, default: 1 },
    showSearch: { type: Boolean, default: true },
    searchPlaceholder: { type: String, default: "Search by name" },
    showCreateButton: { type: Boolean, default: false },
    createLabel: { type: String, default: "New" },
    showSlot: { type: Boolean, default: false },
  },
  emits: ["create", "search", "clear", "paginate", "update:currentPage"],
  data() {
    return {
      internalSearch: "",
      internalPage: this.currentPage,
    };
  },
  watch: {
    currentPage(val) {
      this.internalPage = val;
    },
  },
  methods: {
    onSearch() {
      this.$emit("search", this.internalSearch);
    },
    onClear() {
      this.internalSearch = "";
      this.$emit("clear");
    },
  },
};
</script>

<style lang="scss" scoped>
.data-table {
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  &__header {
    display: flex;
    justify-content: space-between;
    flex-direction: row;
    flex-wrap: wrap;
    &--button {
      @include submit-button;
    }
  }
  &__input {
    border-radius: 4px;
    margin-right: 0.5rem;
  }
  &__container-filter {
    display: flex;
    align-items: center;
    width: 55%;
    flex-direction: row;
    justify-content: flex-end;
    &--dropdown {
      height: 30px;
      display: flex;
      align-items: center;
      margin: 0 0.5rem;
    }
    &--button-filter {
      @include submit-button;
      width: 45%;
      margin-right: 0.5rem;
    }
  }
  &__container-filter-search {
    width: 80%;
    &--input-text {
      border-radius: 8px 0 0 8px;
      border: none;
    }
    &--button {
      @include submit-button;
      border-color: $color-submit-button;
      border-radius: 0px 8px 8px 0;
    }
    &--button-clear {
      background-color: $color-white;
      border-color: $color-white;
      color: gray;
      border: 0;
      &:hover, &:active {
        background-color: $color-white !important;
        border-color: $color-white !important;
        color: gray !important;
      }
    }
  }
  &__chip {
    display: inline-block;
    width: auto;
    height: auto;
    padding: 5px 15px;
    background-color: #f1f1f1;
    border-radius: 25px;
    margin: 0.2rem;
    font-size: 12px;
  }
  &__actions {
    display: flex;
    align-items: center;
    justify-content: center;
    span {
      cursor: pointer;
    }
  }
}
:deep([role="button"]) {
  background-color: transparent;
  line-height: auto;
}
:deep(.p-datatable-header) {
  background-color: $color-background-dark !important;
}
:deep(.p-datatable) {
  width: 96%;
}
:deep(.p-column-header-content) {
  justify-content: start;
}
:deep(.pagination) {
  padding-bottom: 0.625rem;
}
.pi-pencil {
  color: #17a2b8;
  padding-right: 0.5rem;
}
.pi-trash {
  color: #c60707;
  padding-right: 0.5rem;
}

@media (max-width: 912px) and (min-width: 761px) {
  .data-table {
    &__container-filter {
      width: 70%;
    }
  }
}

@media (max-width: 760px) and (min-width: 661px) {
  .data-table {
    &__container-filter {
      margin: 10px;
      width: 70%;
      flex-direction: column;
      align-items: flex-end;
    }
    &__container-filter--button-filter {
      width: 50%;
      margin-bottom: 0.8rem;
      margin-right: 0;
    }
    &__input {
      margin-bottom: 0.5rem;
    }
    &__container-filter-search {
      width: 100%;
    }
    &__header--button {
      margin: 10px;
    }
  }
}

@media (max-width: 660px) {
  .data-table {
    &__header {
      flex-direction: column;
      align-items: flex-start;
      &--button {
        margin: 0 0 10px 10px;
      }
    }
    &__container-filter {
      flex-direction: column;
      justify-content: flex-start;
      align-items: flex-start;
      width: 96%;
      margin: 0 0 0 10px;
    }
    &__container-filter--button-filter {
      width: 13.13rem;
      margin-bottom: 0.8rem;
      margin-right: 0;
    }
    &__input {
      margin-bottom: 0.5rem;
    }
    &__container-filter-search {
      width: 100%;
    }
  }
}
</style>
